import React from "react";
import { ReactComponent as MailMark } from "../../../assets/svg/plan/mail.svg";
import { ReactComponent as WhatsappMark } from "../../../assets/svg/plan/whatsapp.svg";
import { ReactComponent as CallMark } from "../../../assets/svg/plan/call.svg";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { formatPrice, planInclusions } from "../planFormat";
import {
  BrandPlan,
  PlanStatus,
  SUPPORT_CHANNELS,
  SupportChannel,
} from "../types/brand.types";

const STATUS_STYLES: Record<PlanStatus, string> = {
  Active: "bg-brand-soft text-[#00899B]",
  "In-active": "bg-[#F1F4F6] text-ink-muted",
};

/**
 * The Support column is icons rather than words — three of them fit in a cell
 * that three names would not. Each is named for anyone who cannot see it.
 *
 * The drawn marks, each authored at 18px with its stroke already in ink-muted,
 * so the cell has nothing left to colour them with.
 */
const CHANNEL_ICONS: Record<SupportChannel, React.ElementType> = {
  EMAIL: MailMark,
  WHATSAPP: WhatsappMark,
  CALL: CallMark,
};

const COLUMNS = [
  "Plan name",
  "Duration",
  "Price",
  "Support",
  "Created on",
  "Status",
];

const bodyCell = "px-4 sm:px-6 py-4 text-[14px] text-ink";

interface PlansTableProps {
  plans: BrandPlan[];
  /** Stands in for the rows when the list comes back with none. */
  empty: EmptyStateProps;
  /**
   * Where the list is. The body says nothing while it is on its way, and only
   * a wait long enough to notice shimmers, as one block the size of the table.
   * Rows already in hand are left alone either way: a table with something to
   * show goes on showing it while the next answer is fetched.
   */
  phase?: LoadPhase;
  onEdit: (plan: BrandPlan) => void;
  /** Asks to list the plan on the public page, or to take it off again. */
  onToggleStatus: (plan: BrandPlan) => void;
}

const PlansTable: React.FC<PlansTableProps> = ({
  plans,
  empty,
  phase = "ready",
  onEdit,
  onToggleStatus,
}) =>
  phase !== "ready" && plans.length === 0 ? (
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
  // The only scroll region on the tab: rows move, the sticky header row and
  // the card's pagination footer stay put. `min-h-0` is what lets this shrink
  // inside the card's flex column instead of pushing the footer off-screen.
  <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
    {/* Full height only when the rows are gone — see `ClientsTable`. */}
    <table
      className={`w-full min-w-[880px] table-fixed border-collapse text-left ${
        plans.length === 0 ? "h-full" : ""
      }`}
    >
      <colgroup>
        <col className="w-[26%]" />
        <col className="w-[13%]" />
        <col className="w-[13%]" />
        <col className="w-[12%]" />
        <col className="w-[15%]" />
        <col className="w-[14%]" />
        <col className="w-[7%]" />
      </colgroup>
      {/* The header pins to the top of this container. Its bottom rule is an
          inset shadow rather than a border because collapsed table borders
          scroll away from a sticky cell. */}
      <thead className="sticky top-0 z-10">
        <tr>
          {COLUMNS.map((heading) => (
            <th
              key={heading}
              scope="col"
              className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
            >
              {heading}
            </th>
          ))}
          <th
            scope="col"
            className="bg-white px-4 sm:px-6 py-3 shadow-[inset_0_-1px_0_#E9EDF1]"
          >
            <span className="sr-only">Actions</span>
          </th>
        </tr>
      </thead>
      {plans.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={COLUMNS.length + 1} className="align-middle">
              <EmptyState {...empty} />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {plans.map((plan) => (
            <tr
              key={plan.id}
              className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors"
            >
              <td className="px-4 sm:px-6 py-4">
                <p className="truncate text-[14px] text-ink">{plan.name}</p>
                <PlanInclusions plan={plan} />
              </td>
              <td className={bodyCell}>{plan.duration}</td>
              <td className={bodyCell}>{formatPrice(plan.price)}</td>
              <td className="px-4 sm:px-6 py-4">
                <SupportChannels channels={plan.supportChannels} />
              </td>
              <td className={bodyCell}>{plan.createdOn}</td>
              <td className="px-4 sm:px-6 py-4">
                <span
                  className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                    STATUS_STYLES[plan.status]
                  }`}
                >
                  {plan.status}
                </span>
              </td>
              <td className="px-4 sm:px-6 py-4">
                <RowActionsMenu
                  // The whole row identifies the plan: the same name and
                  // duration can appear twice at different prices, so the
                  // price is what makes the label unambiguous.
                  label={`Actions for ${plan.name}, ${
                    plan.duration
                  }, ${formatPrice(plan.price)}`}
                  // Delete is not offered: there is no endpoint behind it, and
                  // deactivating is what takes a plan off the public page.
                  actions={[
                    { label: "Edit", onSelect: () => onEdit(plan) },
                    {
                      label:
                        plan.status === "Active" ? "Deactivate" : "Activate",
                      destructive: plan.status === "Active",
                      onSelect: () => onToggleStatus(plan),
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </tbody>
      )}
    </table>
  </div>
  );

/**
 * What the plan includes, under its name: the counts it puts a number on,
 * separated by bars. Nothing at all where it answers none of them, rather than
 * an empty line holding the rows below it apart.
 */
const PlanInclusions: React.FC<{ plan: BrandPlan }> = ({ plan }) => {
  const parts = planInclusions(plan);
  if (parts.length === 0) return null;

  return (
    <p className="mt-1 truncate text-[12px] text-ink-muted">
      {parts.join(" | ")}
    </p>
  );
};

/**
 * The channels a plan is supported over, one icon each.
 *
 * Drawn in `SUPPORT_CHANNELS`' order rather than the order they were picked in,
 * so the same pair of channels sits the same way in every row and the column
 * can be read down. A code this app has no icon for is left out of the cell —
 * the edit dialog still carries it, so it is hidden here, not lost.
 */
const SupportChannels: React.FC<{ channels: SupportChannel[] }> = ({
  channels,
}) => {
  const shown = SUPPORT_CHANNELS.filter((channel) =>
    channels.includes(channel.value)
  );

  if (shown.length === 0) {
    return (
      <span className="text-[14px] text-ink-subtle">
        {/* An em dash, so the cell reads as answered-with-none rather than as
            a column that failed to render. */}
        —<span className="sr-only">No support channels</span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2.5">
      {shown.map(({ value, label }) => {
        const Mark = CHANNEL_ICONS[value];
        return (
          <Mark
            key={value}
            role="img"
            aria-label={label}
            className="h-[18px] w-[18px]"
          />
        );
      })}
    </span>
  );
};

export default PlansTable;
